import { numberWithCommas } from "@fla/common";
import {
  AustraliaMapIcon,
  Button,
  CheckIcon,
  CloseIcon,
  HomeFillIcon,
  IncDec,
  InvestmentsIcon,
  OverseasIcon,
  SuperSplitIcon
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import { GetStarted_SummaryServiceSchema } from "definitions";
import { useGetStarted } from "hooks";
import { GetStartedStoreTypes } from "interfaces";
import { FC, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface IProps {
  formId: string;
  store: GetStartedStoreTypes;
  onSubmit: SubmitHandler<GetStartedStoreTypes>;
  upgradeToFullService?: () => void;
  goBackToEssentialPackage?: () => void;
  startOver?: () => void;
}

const FullService: FC<IProps> = ({ formId, store, onSubmit, goBackToEssentialPackage }) => {
  const {
    handleSubmit,
    watch,
    register,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: store,
    resolver: yupResolver(GetStarted_SummaryServiceSchema)
  });

  const { outOfTime, fullServiceRequired } = useGetStarted();

  const totalLegalFees = useMemo(() => {
    let basePrice = 2750;
    if (watch("finance.real_estate_num") > 2) {
      basePrice += 330 * (watch("finance.real_estate_num") - 2);
    }

    if (watch("finance.super_split_num") > 1) {
      basePrice += 330 * (watch("finance.super_split_num") - 1);
    }

    if (watch("finance.business_nt_num") > 1) {
      basePrice += 330 * (watch("finance.business_nt_num") - 1);
    }

    if (watch("finance.business_trust_num") > 0) {
      basePrice += 550 * watch("finance.business_trust_num");
    }

    if (watch("finance.trust_num") > 0) {
      basePrice += 550 * watch("finance.trust_num");
    }

    return basePrice;
  }, [watch()]);

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(v => onSubmit({ ...v, legal_fees: totalLegalFees, package: "full" }))}
      className="space-y-10"
    >
      <div className="mb-8 p-10 border-b space-y-3 bg-secondary text-white rounded-t-lg">
        <h2 className="text-center">Full Service Package</h2>
        <p className="text-center text-xs">
          The Full Service package is suitable if there’s a trust or self-managed super fund (even if it’s not being
          divided), if you can’t easily communicate with your partner, if you’ve already lodged consent order documents
          with the court, if there’s combined net assets over $1.5 million, if there is a risk of the deal being unfair
          or for those with more complex situations.
        </p>
      </div>

      <section className="space-y-5 px-10">
        <ul className="space-y-5 my-8">
          <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
            <div className="w-8">
              <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
            </div>
            <div className="w-[calc(100%_-_2rem] pl-5">
              <p className="font-bold">More extensive legal advice</p>
              <p className="text-sm text-gray-600 font-light">
                Our legal advice will extend to more than just preparing the court documents. We will give you
                additional advice on whether the agreement is fair. This will give you an idea of whether the court will
                question your agreement.
              </p>
            </div>
          </li>

          <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
            <div className="w-8">
              <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
            </div>
            <div className="w-[calc(100%_-_2rem] pl-5">
              <p className="font-bold">Dealing with court requisitions</p>
              <p className="text-sm text-gray-600 font-light">
                The Full Service includes us responding to court requisitions (if they are issued).
              </p>
            </div>
          </li>

          <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
            <div className="w-8">
              <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
            </div>
            <div className="w-[calc(100%_-_2rem] pl-5">
              <p className="font-bold">We can communicate with your partner</p>
              <p className="text-sm text-gray-600 font-light">
                We can correspond with your partner in respect to getting the document signed and obtaining their
                financial information.
              </p>
            </div>
          </li>

          <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
            <div className="w-8">
              <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
            </div>
            <div className="w-[calc(100%_-_2rem] pl-5">
              <p className="font-bold">We prepare the court documents</p>
              <p className="text-sm text-gray-600 font-light">
                This includes drafting the Consent Orders Application and the minute
              </p>
            </div>
          </li>

          <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
            <div className="w-8">
              <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
            </div>
            <div className="w-[calc(100%_-_2rem] pl-5">
              <p className="font-bold">We will lodge it with the court on your behalf</p>
              <p className="text-sm text-gray-600 font-light">
                You don't need to worry about the lodgement complexities. You just need to return the signed documents
                to us.
              </p>
            </div>
          </li>
          {store.relationship.bfa_exists === "yes" ? (
            <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold">Binding Financial Agreement Exists</p>
                <p className="text-sm text-gray-600 font-light">
                  As a BFA exists, your matter is more complicated and you'll need additional assistance. Therefore, the
                  full service package is appropriate for your circumstances.
                </p>
              </div>
            </li>
          ) : null}
          <p className="font-bold">Your Combined Assets</p>
          <p className="text-sm text-gray-600 font-light">
            Please specify the assets you or your partner own, even if it's not being divided.
          </p>

          {/* Real Estate  */}
          {store?.finance?.real_estate === "no" ? (
            <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <CloseIcon classes="w-7 text-red-500" />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold">No Real Estate </p>
                <p className="text-sm text-gray-600 font-light">You have stated neither of you have any real estate.</p>
              </div>
            </li>
          ) : (
            <IncDec
              register={register}
              watch={watch}
              name="finance.real_estate_num"
              id="finance.real_estate_num"
              title="How many properties do you both own?"
              subTitle="Include all real estate owned by you and your partner, even if it’s not being divided."
              setValue={setValue}
              error={errors?.finance?.real_estate_num?.message ?? ""}
              icon={HomeFillIcon}
            />
          )}

          {/* Super Split */}
          {store?.finance?.super_split === "no" ? (
            <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <CloseIcon classes="w-7 text-red-500" />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold">No Super Split Required </p>
                <p className="text-sm text-gray-600 font-light">
                  You do not intend to transfer any superannuation between your super funds.
                </p>
              </div>
            </li>
          ) : (
            <IncDec
              register={register}
              watch={watch}
              name="finance.super_split_num"
              id="finance.super_split_num"
              title="How many super splits are needed?"
              subTitle="You only need a super split if you’re transferring super from one person’s super account to the person’s super account."
              setValue={setValue}
              error={errors?.finance?.super_split_num?.message}
              icon={SuperSplitIcon}
            />
          )}
          {store?.finance?.joint_assets_1_5m_exceed === "yes" ? (
            <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <InvestmentsIcon />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold">Net assets over $1.5 million</p>
                <p className="text-sm text-gray-500 font-light">
                  The value of your combined net assets (eg assets minus debts) exceed this amount.
                </p>
              </div>
            </li>
          ) : null}
          {/* Overseas Assets  */}
          {store?.finance?.overseas_assets === "no" ? (
            <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <AustraliaMapIcon />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold">All your assets and debts are within Australia</p>
                <p className="text-sm text-gray-500 font-light">
                  You and your partner do not have any assets or debts overseas.
                </p>
              </div>
            </li>
          ) : (
            <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <OverseasIcon strokeColor="w-8 h-auto" />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold">Overseas assets (included free of charge)</p>
                <p className="text-sm text-gray-600 font-light">
                  You mentioned that you have assets overseas. This is will not incur any extra fees in our Full Service
                  package. We cannot give legal advice on overseas assets, but we can list them in the consent orders.
                </p>
              </div>
            </li>
          )}
        </ul>

        {/* Business No Trust  */}
        {watch("finance.business") === "yes" ? (
          <IncDec
            register={register}
            watch={watch}
            name="finance.business_nt_num"
            id="finance.business_nt_num"
            title="How many businesses (without a trust) do you both own?"
            subTitle="Include it, even if you’re not dividing it."
            setValue={setValue}
            error={errors?.finance?.business_nt_num?.message}
          />
        ) : null}

        {/* Business Trust  */}
        {watch("finance.business_trust") === "yes" ? (
          <IncDec
            register={register}
            watch={watch}
            name="finance.business_trust_num"
            id="finance.business_trust_num"
            title="How many businesses (linked to a trust) are there?"
            subTitle="Eg ABC Pty Ltd as trustee for the Smith Family Trust. Include every trust either of you have an interest in, even if you’re not dividing it."
            setValue={setValue}
            error={errors?.finance?.business_trust_num?.message}
          />
        ) : null}

        {/* Trust  */}
        {watch("finance.other_trust") === "yes" ? (
          <IncDec
            register={register}
            watch={watch}
            name="finance.trust_num"
            id="finance.trust_num"
            title="How many (non-business) trusts do you both have an interest in?"
            subTitle="Eg: Family Trust and Unit Trust. Include every trust either of you have an interest in, even if you’re not dividing it."
            setValue={setValue}
            error={errors?.finance?.trust_num?.message}
          />
        ) : null}

        {/* Out-Of-Time  */}

        {outOfTime(store) ? (
          <div className="space-y-3 my-8">
            <p className="font-bold">Other Assistance</p>
            <div className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold">Out-Of-Time Extension Included (For Free)</p>
                <p className="text-sm text-gray-500 font-light">
                  The deadline to lodge consent orders has expired. The good news is you can still apply. Our Full
                  Service package includes the additional work needed to get your extension approved by the court.
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Full Service Required  */}

        {fullServiceRequired(store) ? (
          <ul className="space-y-5 my-8">
            <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold"> Full Service Package Required </p>
                <p className="text-sm text-gray-500 font-light">
                  The Essentials package is not suitable if there is a trust or self-managed super fund (even if it’s
                  not being divided, who have already lodged consent order documents with the court to divide property
                  (or have been requisitioned), or if there are combined net assets over $1.5 million.
                </p>
              </div>
            </li>
          </ul>
        ) : null}
        <div className="pb-8">
          <div className="pb-5 flex flex-col justify-center items-center space-y-3">
            <p className="font-bold">Legal fees for the above work</p>
            <p className="font-bold text-2xl">${numberWithCommas(totalLegalFees)}</p>
            <p className="text-xs text-gray-600">including GST</p>
          </div>
        </div>
        {fullServiceRequired(store) ? null : (
          <Button
            fontSize="lg"
            borderRadius="xl"
            form="onboarding-form"
            className={classNames(
              "w-fit font-semibold px-10 py-2 flex justify-center items-center bg-primary/20 !text-primary hover:bg-primary hover:!text-white transition-all mx-auto"
            )}
            onClick={goBackToEssentialPackage}
            type="button"
          >
            Go Back to Essentials Only Package
          </Button>
        )}
      </section>
    </form>
  );
};

export default FullService;
